// Account input intelligence helpers.
//
// `detectAmbiguity` runs a lightweight heuristic on company names to flag generic
// or commonly duplicated names. When true, the UI nudges the user to provide an
// official website URL — this anchors RADAR's research on the correct entity.
//
// `normalizeUrl` accepts forgiving inputs (company.com, www.company.com,
// https://company.com/about) and returns a canonical {url, domain} or null.

const GENERIC_TOKENS = [
  "tech", "technologies", "solutions", "systems", "global", "vision",
  "consulting", "services", "group", "holdings", "industries", "innovation",
  "international", "enterprises", "ventures", "partners", "labs", "digital",
  "corp", "corporation", "co", "inc", "ltd", "plc", "llc",
];

const KNOWN_UNIQUE_BRANDS = new Set([
  "siemens", "boeing", "caterpillar", "schneider electric", "schneider",
  "bosch", "honeywell", "ge", "general electric", "rolls-royce", "rolls royce",
  "tata motors", "tata", "mahindra", "reliance", "infosys", "tcs", "wipro",
  "airbus", "lockheed martin", "raytheon", "northrop grumman", "embraer",
  "thyssenkrupp", "danfoss", "abb", "emerson", "rockwell", "yokogawa",
  "mitsubishi", "hitachi", "fanuc", "kuka", "stanley black & decker",
  "ford", "gm", "toyota", "volkswagen", "vw", "bmw", "daimler", "mercedes",
  "stellantis", "hyundai", "kia", "nissan", "honda", "mazda", "subaru",
  "saab", "scania", "volvo", "iveco", "freightliner", "paccar",
  "samsung", "lg", "panasonic", "sony", "philips", "siemens healthineers",
  "apple", "microsoft", "google", "amazon", "meta", "facebook", "netflix",
  "ibm", "oracle", "sap", "salesforce", "adobe", "intel", "amd", "nvidia",
  "qualcomm", "broadcom", "cisco", "dell", "hp", "lenovo",
]);

/**
 * Detect whether a company name is "ambiguous" — likely shared by multiple
 * companies, or generic enough that AI research could pull the wrong entity.
 *
 * Returns { ambiguous: boolean, reason: string }
 */
export function detectAmbiguity(rawName) {
  const name = (rawName || "").trim();
  if (!name) return { ambiguous: false, reason: "" };

  const lower = name.toLowerCase();

  // Highly recognisable brands → never ambiguous
  if (KNOWN_UNIQUE_BRANDS.has(lower)) {
    return { ambiguous: false, reason: "" };
  }

  const tokens = lower.split(/\s+/).filter(Boolean);

  // Very short single-word name (≤ 3 chars) — likely an acronym → ambiguous
  if (tokens.length === 1 && tokens[0].length <= 3) {
    return { ambiguous: true, reason: "Short acronyms often clash with other companies." };
  }

  // Single generic token like "Solutions" or "Tech" → ambiguous
  if (tokens.length === 1 && GENERIC_TOKENS.includes(tokens[0])) {
    return { ambiguous: true, reason: "This name is highly generic." };
  }

  // Multi-word but all tokens are generic ("Global Solutions", "Vision Systems")
  const genericCount = tokens.filter(t => GENERIC_TOKENS.includes(t)).length;
  if (tokens.length >= 2 && genericCount >= tokens.length - 1) {
    return {
      ambiguous: true,
      reason: "Many companies share names like this — a website URL ensures accuracy.",
    };
  }

  // Common single-word real-word names (e.g. "Vision", "Apex", "Pinnacle")
  if (tokens.length === 1 && /^[a-z]{4,8}$/.test(tokens[0])) {
    return {
      ambiguous: true,
      reason: "Single-word names can match multiple companies.",
    };
  }

  return { ambiguous: false, reason: "" };
}

/**
 * Normalize a user-provided URL string. Accepts loose inputs like:
 *   - company.com
 *   - www.company.com
 *   - https://company.com
 *   - https://www.company.com/about
 *
 * Returns { url, domain } when valid, or null otherwise.
 */
export function normalizeUrl(raw) {
  const s = (raw || "").trim().replace(/^['"]+|['"]+$/g, "");
  if (!s) return null;

  let candidate = s;
  if (!/^https?:\/\//i.test(candidate)) {
    candidate = "https://" + candidate;
  }

  try {
    const u = new URL(candidate);
    const host = u.hostname.toLowerCase();
    if (!host || !host.includes(".")) return null;
    // Reject obviously invalid hostnames (no TLD, single label, etc.)
    if (host.split(".").pop().length < 2) return null;
    const domain = host.startsWith("www.") ? host.slice(4) : host;
    if (domain.length < 4) return null;
    return {
      url: `${u.protocol}//${host}${u.pathname.replace(/\/$/, "")}`,
      domain,
    };
  } catch {
    return null;
  }
}

/**
 * Heuristic mismatch check between the typed company name and the website domain.
 * Returns true ONLY when we are fairly confident the two refer to different
 * entities (used to gate a "Please confirm" prompt before generation).
 */
export function nameDomainMismatch(companyName, domain) {
  if (!companyName || !domain) return false;
  const nameTokens = companyName
    .toLowerCase()
    .split(/[\s\-_&,.()]+/)
    .filter(t => t && t.length >= 3 && !GENERIC_TOKENS.includes(t));
  if (nameTokens.length === 0) return false;

  const domainSlug = domain.split(".")[0].toLowerCase();
  // Any company token that overlaps with the domain slug (substring either way) → match
  for (const t of nameTokens) {
    if (domainSlug.includes(t) || t.includes(domainSlug)) return false;
  }
  return true;
}
